import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BeforeAfterSlider = ({ beforeImage, afterImage, beforeLabel, afterLabel }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const handleMouseDown = () => {
    isDragging.current = true;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleTouchMove = (e) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full aspect-[4/3] overflow-hidden cursor-ew-resize select-none rounded-lg shadow-xl"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchMove={handleTouchMove}
    >
      {/* After Image (Background) */}
      <img 
        src={afterImage} 
        alt={afterLabel}
        className="absolute inset-0 w-full h-full object-cover"
        draggable="false"
      />
      
      {/* Before Image (Clipped) */}
      <div 
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img 
          src={beforeImage} 
          alt={beforeLabel}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ 
            width: containerRef.current ? containerRef.current.offsetWidth : '100%',
            maxWidth: 'none'
          }}
          draggable="false"
        />
      </div>
      
      {/* Slider Handle */}
      <div 
        className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10"
        style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        {/* Handle Circle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
          <div className="flex items-center gap-1">
            <svg className="w-3 h-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <svg className="w-3 h-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
      
      {/* Labels */}
      <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 text-sm font-medium rounded">
        {beforeLabel}
      </div>
      <div className="absolute top-4 right-4 bg-white/90 text-black px-3 py-1 text-sm font-medium rounded">
        {afterLabel}
      </div>
    </div>
  );
};

// Default fallback comparisons
const DEFAULT_COMPARISONS = [
  {
    before_image: 'https://images.pexels.com/photos/3125905/pexels-photo-3125905.jpeg',
    after_image: 'https://images.unsplash.com/photo-1680503397107-475907e4f3e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxzdGFnZWQlMjBsaXZpbmclMjByb29tJTIwcHJvZmVzc2lvbmFsJTIwaW50ZXJpb3IlMjBwaG90b2dyYXBoeXxlbnwwfHx8fDE3Njg5MTg5NTV8MA&ixlib=rb-4.1.0&q=85',
    title_de: 'Wohnzimmer Staging',
    title_ru: 'Стейджинг гостиной',
    description_de: 'Professionelle Fotografie zeigt Räume größer und einladender',
    description_ru: 'Профессиональная фотография делает комнаты больше и уютнее'
  },
  {
    before_image: 'https://images.unsplash.com/photo-1762810951632-68c9f197cf33?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxlbXB0eSUyMHJvb20lMjB1bmZ1cm5pc2hlZCUyMGFwYXJ0bWVudHxlbnwwfHx8fDE3Njg5MTg5NTN8MA&ixlib=rb-4.1.0&q=85',
    after_image: 'https://images.unsplash.com/photo-1676241340792-d258a62ce814?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxzdGFnZWQlMjBsaXZpbmclMjByb29tJTIwcHJvZmVzc2lvbmFsJTIwaW50ZXJpb3IlMjBwaG90b2dyYXBoeXxlbnwwfHx8fDE3Njg5MTg5NTV8MA&ixlib=rb-4.1.0&q=85',
    title_de: 'Lichtoptimierung',
    title_ru: 'Оптимизация освещения',
    description_de: 'HDR-Technik für perfekte Belichtung in jedem Raum',
    description_ru: 'HDR-технология для идеального освещения в каждой комнате'
  }
];

const BeforeAfterSection = () => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const [comparisons, setComparisons] = useState(DEFAULT_COMPARISONS);

  useEffect(() => {
    loadComparisons();
  }, []);

  const loadComparisons = async () => {
    try {
      const response = await fetch('/api/public/before-after');
      const data = await response.json();
      if (data.items && data.items.length > 0) {
        setComparisons(data.items);
      }
    } catch (error) {
      console.error('Error loading before/after items:', error);
    }
  };

  if (comparisons.length === 0) return null;

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 
            className="text-4xl md:text-5xl font-bold mb-4 text-white"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {currentLang === 'de' ? 'Der Unterschied' : 'Разница очевидна'}
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {currentLang === 'de' 
              ? 'Sehen Sie den Unterschied zwischen Smartphone-Fotos und professioneller Immobilienfotografie'
              : 'Увидьте разницу между фото на смартфон и профессиональной съёмкой недвижимости'
            }
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {comparisons.map((comparison, index) => (
            <div key={index} className="space-y-4">
              <BeforeAfterSlider
                beforeImage={comparison.before_image}
                afterImage={comparison.after_image}
                beforeLabel={currentLang === 'de' ? 'Vorher' : 'До'}
                afterLabel={currentLang === 'de' ? 'Nachher' : 'После'}
              />
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-2">
                  {currentLang === 'de' ? comparison.title_de : comparison.title_ru}
                </h3>
                <p className="text-gray-400">
                  {currentLang === 'de' ? comparison.description_de : comparison.description_ru}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-300 mb-6">
            {currentLang === 'de' 
              ? 'Professionelle Fotos verkaufen Immobilien bis zu 32% schneller und zu höheren Preisen'
              : 'Профессиональные фото продают недвижимость до 32% быстрее и по более высоким ценам'
            }
          </p>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterSection;
